import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  autocompleteQuestions,
  helpEntryFor,
  helpPages,
  helpSuggestions,
  helpViewFor,
  matchPages,
  pathForView,
  questionBank,
  rankHelp,
  stitchHelp,
  viewFromDesign,
  type HelpHit,
  type HelpPreset,
  type HelpSource,
  type ViewId,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../session/SessionContext';
import { CONSOLE_INDEX } from '../content/consoleIndex';
import { CONSOLE_FAQ } from '../content/consoleFaq';
import { sx } from '../ui/designStyles';

/**
 * The Help drawer (the ? in the top bar, or the ? key), ported from the design
 * (T17995-18121, hx* L33462-33862). Suggested questions answer from the console's own
 * help text at once. A typed question goes to POST /api/aithora/help/ask, which answers
 * only from the help docs and logs the question; when no written answer comes back (no
 * model, or the server is unreachable) the drawer stitches the answer from the matching
 * help text, as the design does, and says where it came from. Nothing outside the help
 * text is ever asserted.
 */

const CONTACT = 'info@aithoracare.com';
const NONE_TEXT = 'The help does not cover that, and I will not guess. Try naming the page or the badge you are looking at.';

type Source = { label: string; view: ViewId | null; tab?: string };
type Msg =
  | { who: 'user'; text: string }
  | { who: 'bot'; id: number; paras: string[]; hits: HelpHit[]; sources: Source[]; mode: 'ai' | 'help' | 'none'; askId?: string; fb?: 'yes' | 'no' };

const S = {
  layer: 'position:fixed;inset:0;z-index:72;display:flex;justify-content:flex-end;pointer-events:none;',
  panel: "position:relative;pointer-events:auto;width:400px;max-width:92vw;flex:0 0 auto;font-family:'Archivo',system-ui,sans-serif;background:#FBF8F1;border-left:1px solid rgba(34,39,46,.16);box-shadow:-12px 0 36px rgba(34,39,46,.12);display:flex;flex-direction:column;min-height:0;animation:hxIn .22s cubic-bezier(.22,.61,.36,1);",
  head: 'flex:0 0 auto;display:flex;align-items:center;gap:10px;padding:14px 14px 13px 18px;border-bottom:1px solid rgba(34,39,46,.11);',
  titles: 'display:flex;flex-direction:column;gap:1px;min-width:0;',
  // No colour in the design: its drawer sits outside the console root, so the title takes
  // the page default, black. Copied as drawn.
  title: 'font-size:15px;font-weight:700;letter-spacing:-.01em;line-height:1.2;color:#000;',
  sub: 'font-size:14px;color:#54606C;line-height:1.2;',
  more: 'display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:7px;color:#54606C;text-decoration:none;font-size:17px;font-weight:700;letter-spacing:1px;',
  x: 'display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:7px;color:#54606C;text-decoration:none;font-size:18px;',
  menu: 'position:absolute;top:52px;right:14px;z-index:3;min-width:210px;background:#FCFAF4;border:1px solid rgba(34,39,46,.16);border-radius:10px;box-shadow:0 12px 30px rgba(34,39,46,.14);padding:5px;display:flex;flex-direction:column;',
  menuItem: 'display:flex;flex-direction:column;gap:2px;padding:8px 10px;border-radius:7px;text-decoration:none;color:#22272E;',
  menuLabel: 'font-size:13px;font-weight:600;',
  menuNote: 'font-size:14px;color:#54606C;line-height:1.35;',
  scroll: 'flex:1 1 auto;min-height:0;overflow-y:auto;padding:22px 18px 14px;display:flex;flex-direction:column;gap:18px;',
  greetWrap: 'flex:0 0 auto;display:flex;flex-direction:column;gap:4px;',
  greet: 'font-size:19px;font-weight:800;letter-spacing:-.02em;line-height:1.25;color:#22272E;text-wrap:pretty;',
  intro: 'font-size:16px;color:#54606C;line-height:1.5;text-wrap:pretty;',
  introLink: 'color:#2F5C97;font-weight:600;text-decoration:none;',
  sugList: 'flex:0 0 auto;display:flex;flex-direction:column;border-top:1px solid rgba(34,39,46,.10);',
  sug: 'display:flex;align-items:center;gap:10px;padding:11px 2px;border-bottom:1px solid rgba(34,39,46,.10);color:#22272E;font-size:13px;font-weight:600;line-height:1.4;text-decoration:none;',
  arrow: 'flex:0 0 auto;color:#2F5C97;font-size:14px;',
  userWrap: 'flex:0 0 auto;display:block;text-align:right;',
  user: 'display:inline-block;box-sizing:border-box;max-width:86%;text-align:left;vertical-align:top;padding:9px 14px;border-radius:16px 16px 4px 16px;background:#22272E;color:#FCFAF4;font-size:13px;font-weight:500;line-height:1.5;',
  botWrap: 'flex:0 0 auto;display:block;animation:riseIn .32s cubic-bezier(.22,.61,.36,1) backwards;',
  botRow: 'display:flex;align-items:flex-start;gap:10px;min-width:0;',
  mark: 'width:22px;height:22px;flex:0 0 22px;object-fit:contain;margin-top:1px;',
  botCol: 'flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:10px;',
  para: 'font-size:13.5px;line-height:1.6;color:#22272E;text-wrap:pretty;',
  goRow: 'display:flex;align-items:center;gap:6px 8px;flex-wrap:wrap;',
  go: 'display:inline-flex;align-items:center;gap:6px;height:30px;padding:0 12px;border-radius:8px;background:#2F5C97;color:#FFFFFF;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;',
  foot: 'display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;padding-top:8px;border-top:1px solid rgba(34,39,46,.08);font-size:14px;color:#54606C;',
  tag: 'display:inline-flex;align-items:center;font-size:14px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;white-space:nowrap;color:#54606C;',
  src: 'color:#3C454F;font-weight:600;text-decoration:none;white-space:nowrap;border-bottom:1px solid rgba(34,39,46,.24);',
  fbBtn: 'display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:7px;color:#54606C;text-decoration:none;',
  follow: 'display:flex;flex-direction:column;border-top:1px solid rgba(34,39,46,.08);',
  followItem: 'display:flex;align-items:center;gap:10px;padding:8px 2px;border-bottom:1px solid rgba(34,39,46,.08);color:#3C454F;font-size:16px;font-weight:600;line-height:1.4;text-decoration:none;',
  contact: 'font-size:16px;color:#3C454F;line-height:1.5;',
  contactLink: 'font-weight:700;color:#2F5C97;text-decoration:none;',
  busy: 'display:flex;align-items:center;gap:10px;',
  bottom: 'flex:0 0 auto;padding:10px 16px 14px;border-top:1px solid rgba(34,39,46,.09);',
  auto: 'display:flex;flex-direction:column;margin-bottom:9px;border-top:1px solid rgba(34,39,46,.08);',
  autoItem: 'display:flex;align-items:center;gap:9px;padding:7px 2px;border-bottom:1px solid rgba(34,39,46,.08);color:#22272E;font-size:16px;line-height:1.4;text-decoration:none;',
  jumpRow: 'display:flex;align-items:center;gap:6px 8px;flex-wrap:wrap;margin-bottom:9px;',
  jumpHead: 'font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;',
  jump: 'display:inline-flex;align-items:center;gap:6px;height:28px;padding:0 11px;border-radius:8px;background:#2F5C97;color:#FFFFFF;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;',
  inputBox: 'display:flex;align-items:flex-end;gap:8px;padding:10px 9px 9px 14px;border:1px solid rgba(34,39,46,.18);border-radius:14px;background:#FFFFFF;box-shadow:0 1px 2px rgba(34,39,46,.05);',
  input: "flex:1 1 auto;min-width:0;border:0;outline:none;resize:none;background:transparent;font-family:'Archivo',system-ui,sans-serif;font-size:13.5px;line-height:1.5;color:#22272E;",
  send: (on: boolean) =>
    'display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;flex:0 0 30px;border-radius:50%;text-decoration:none;font-size:15px;font-weight:700;' +
    (on ? 'background:#2F5C97;color:#FFFFFF;' : 'background:rgba(34,39,46,.08);color:#54606C;pointer-events:none;'),
  footNote: 'font-size:14px;color:#54606C;text-align:center;margin-top:8px;line-height:1.4;',
  dots: 'display:inline-flex;gap:4px;padding:6px 0;',
  dot: (i: number) => `width:6px;height:6px;border-radius:50%;background:#2F5C97;animation:hxDot 1.1s ease-in-out ${i * 0.15}s infinite;`,
};

/** A local hit as a source link (design hxSrc). */
function sourceFromHit(h: HelpHit): Source {
  if (h.kind === 'faq') return { label: 'FAQ', view: 'help', tab: 'faq' };
  const v = helpViewFor(h.title);
  return { label: v ? h.title : `${h.title} in the docs`, view: v ?? 'help' };
}
/** A server source as a link: its page when it names one, the docs otherwise. */
function sourceFromServer(s: HelpSource): Source {
  const v = viewFromDesign(typeof s.link?.view === 'string' ? s.link.view : null);
  return { label: s.title, view: v ?? 'help', tab: typeof s.link?.tab === 'string' ? s.link.tab : undefined };
}

let nextId = 1;

export function HelpDrawer({
  view,
  tabLabel,
  ask: handed = null,
  onClose,
}: {
  view: ViewId | null;
  tabLabel: string | null;
  /** A question handed over by a page (useOpenHelp): asked once per `n`. */
  ask?: { q: string; n: number } | null;
  onClose: () => void;
}) {
  const { client } = useSession();
  const { role } = useConsoleRole();
  const navigate = useNavigate();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const here = helpEntryFor(CONSOLE_INDEX, view);
  const pageName = here ? here.t : null;
  const bank = useMemo(() => questionBank(CONSOLE_INDEX, CONSOLE_FAQ, role), [role]);
  const sug = useMemo(() => helpSuggestions(CONSOLE_INDEX, CONSOLE_FAQ, role, view, tabLabel), [role, view, tabLabel]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (menu) setMenu(false);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu, onClose]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, busy]);

  const go = (s: Source) => {
    onClose();
    if (!s.view) return;
    navigate(s.tab ? `${pathForView(s.view)}?tab=${encodeURIComponent(s.tab)}` : pathForView(s.view));
  };
  const mailto = (q: string) => `mailto:${CONTACT}?subject=${encodeURIComponent(`Console help: ${pageName || 'question'}`)}${q ? `&body=${encodeURIComponent(q)}` : ''}`;

  const ask = async (raw: string, preset?: HelpPreset) => {
    const q = raw.trim();
    if (!q || busy) return;
    setInput('');
    const prior = msgs.filter((m): m is Extract<Msg, { who: 'user' }> => m.who === 'user').map((m) => m.text);
    setMsgs((m) => [...m, { who: 'user', text: q }]);
    if (preset) {
      setMsgs((m) => [...m, { who: 'bot', id: nextId++, paras: preset.paras, hits: preset.hits, sources: preset.hits.slice(0, 2).map(sourceFromHit), mode: 'help' }]);
      return;
    }
    // Follow-ups lean on the last question: "what about Policy?" ranks with what came before.
    const prevQ = prior.length ? prior[prior.length - 1] : '';
    let local = rankHelp(CONSOLE_INDEX, CONSOLE_FAQ, role, q, view);
    if (prevQ && q.split(/\s+/).length < 7) {
      const ctx = rankHelp(CONSOLE_INDEX, CONSOLE_FAQ, role, `${q} ${prevQ}`, view);
      if (!local.length || (ctx[0] && ctx[0].title !== local[0]?.title && /\b(it|that|this|those|them|what about|and|also)\b/i.test(q))) local = ctx;
    }
    const localHits: HelpHit[] = local.map((d) => (d.kind === 'faq' ? { kind: 'faq', title: d.title, g: d.g } : { kind: 'page', title: d.title }));
    setBusy(true);
    let bot: Extract<Msg, { who: 'bot' }>;
    try {
      const r = await api.helpAsk(client, { body: { question: q, ...(view ? { page: view } : {}) } });
      const sources = r.sources.slice(0, 2).map(sourceFromServer);
      if (r.answer) {
        const paras = r.answer.replace(/[*#_`]/g, '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
        bot = { who: 'bot', id: nextId++, paras, hits: localHits, sources: sources.length ? sources : localHits.slice(0, 2).map(sourceFromHit), mode: r.answerLive ? 'ai' : 'help', askId: r.id };
      } else if (local.length) {
        bot = { who: 'bot', id: nextId++, paras: stitchHelp(local), hits: localHits, sources: localHits.slice(0, 2).map(sourceFromHit), mode: 'help', askId: r.id };
      } else if (sources.length) {
        bot = { who: 'bot', id: nextId++, paras: [r.reason ?? 'The help docs below cover this.'], hits: [], sources, mode: 'help', askId: r.id };
      } else {
        bot = { who: 'bot', id: nextId++, paras: [NONE_TEXT], hits: [], sources: [], mode: 'none', askId: r.id };
      }
    } catch {
      // Help still works when everything else is down: the answer comes from the help text.
      bot = local.length
        ? { who: 'bot', id: nextId++, paras: stitchHelp(local), hits: localHits, sources: localHits.slice(0, 2).map(sourceFromHit), mode: 'help' }
        : { who: 'bot', id: nextId++, paras: [NONE_TEXT], hits: [], sources: [], mode: 'none' };
    }
    setBusy(false);
    setMsgs((m) => [...m, bot]);
  };

  // A page's question (useOpenHelp) goes through the same ask as a typed one, once each.
  const askedN = useRef(0);
  useEffect(() => {
    if (!handed || handed.n === askedN.current) return;
    askedN.current = handed.n;
    void ask(handed.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handed]);

  const feedback = (id: number, v: 'yes' | 'no') => {
    setMsgs((all) => all.map((m) => (m.who === 'bot' && m.id === id ? { ...m, fb: v } : m)));
    const m = msgs.find((x): x is Extract<Msg, { who: 'bot' }> => x.who === 'bot' && x.id === id);
    if (m?.askId) api.helpFeedback(client, { params: { id: m.askId }, body: { helped: v === 'yes' } }).catch(() => {});
  };

  const asked = msgs.filter((m): m is Extract<Msg, { who: 'user' }> => m.who === 'user').map((m) => m.text);
  const lastBot = msgs.map((m) => m.who).lastIndexOf('bot');
  const hasInput = input.trim() !== '';
  const jump = hasInput ? matchPages(role, input, 3).filter((p) => p.view !== view) : [];
  const auto = autocompleteQuestions(bank, input);

  const menuItems = [
    {
      label: 'Your guide',
      note: 'First 10 minutes and how-to answers for your role',
      run: () => {
        onClose();
        navigate('/guide');
      },
    },
    { label: 'Full docs', note: 'Every page, what it is for and what its badges mean', run: () => go({ label: '', view: 'help' }) },
    { label: 'FAQ', note: 'The questions that come up mid-task', run: () => go({ label: '', view: 'help', tab: 'faq' }) },
    { label: 'Contact a person', note: CONTACT, run: () => window.location.assign(mailto('')) },
    { label: 'Clear this conversation', note: 'Start over', run: () => setMsgs([]) },
  ];

  let lastQ = '';
  return (
    <div style={sx(S.layer)}>
      <div role="dialog" aria-label="Help" style={sx(S.panel)} data-testid="help-drawer">
        <div style={sx(S.head)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2F5C97" strokeWidth="1.8" strokeLinecap="round" style={sx('flex:0 0 18px;')} aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M9.6 9.2a2.5 2.5 0 1 1 3.6 2.3c-.7.4-1.2 1-1.2 1.8" />
            <path d="M12 17h.01" />
          </svg>
          <span style={sx(S.titles)}>
            <span style={sx(S.title)}>Help</span>
            <span style={sx(S.sub)}>Answers from Aithora help</span>
          </span>
          <span style={sx('flex:1;')} />
          <button type="button" className="dlink hx-quiet" title="More" aria-label="More" aria-expanded={menu} style={sx(S.more)} onClick={() => setMenu((o) => !o)}>
            {'···'}
          </button>
          <button type="button" className="dlink hx-quiet" title="Close" aria-label="Close help" style={sx(S.x)} onClick={onClose}>
            {'×'}
          </button>
        </div>
        {menu && (
          <div role="menu" style={sx(S.menu)}>
            {menuItems.map((m) => (
              <button
                key={m.label}
                type="button"
                role="menuitem"
                className="dlink hx-menuitem"
                style={sx(S.menuItem)}
                onClick={() => {
                  setMenu(false);
                  m.run();
                }}
              >
                <span style={sx(S.menuLabel)}>{m.label}</span>
                <span style={sx(S.menuNote)}>{m.note}</span>
              </button>
            ))}
          </div>
        )}
        <div ref={scrollRef} style={sx(S.scroll)} aria-live="polite">
          <div style={sx(S.greetWrap)}>
            <div style={sx(S.greet)}>{pageName ? `What do you need on ${pageName}?` : 'What do you need help with?'}</div>
            <div style={sx(S.intro)}>
              I cover every page, badge and number in the console. Type a page name to jump there. For anything else,{' '}
              <a href={`mailto:${CONTACT}`} style={sx(S.introLink)}>
                {CONTACT}
              </a>
              .
            </div>
          </div>
          {msgs.length === 0 && (
            <div style={sx(S.sugList)}>
              {sug.map((x) => (
                <button key={x.label} type="button" className="dlink hx-link" style={sx(S.sug)} onClick={() => ask(x.label, x.preset)}>
                  <span style={sx('flex:1 1 auto;min-width:0;')}>{x.label}</span>
                  <span style={sx(S.arrow)} aria-hidden="true">
                    {'↗'}
                  </span>
                </button>
              ))}
            </div>
          )}
          {msgs.map((m, i) => {
            if (m.who === 'user') {
              lastQ = m.text;
              return (
                <div key={`u${i}`} style={sx(S.userWrap)}>
                  <span style={sx(S.user)}>{m.text}</span>
                </div>
              );
            }
            const q = lastQ;
            const isLast = i === lastBot;
            const h0 = m.hits[0];
            const follow = isLast && h0
              ? bank.filter((b) => (h0.kind === 'faq' ? b.g && b.g === h0.g : b.preset?.hits[0]?.title === h0.title)).filter((b) => !asked.includes(b.label)).slice(0, 3)
              : [];
            const topView = h0 && h0.kind === 'page' ? helpViewFor(h0.title) : null;
            const goPages: { view: ViewId; name: string }[] = [];
            [...matchPages(role, q, 2), ...helpPages(role).filter((p) => p.view === topView)].forEach((p) => {
              if (p.view !== view && goPages.length < 3 && !goPages.some((x) => x.view === p.view)) goPages.push(p);
            });
            const showFb = !m.fb && m.mode !== 'none';
            const contact = m.fb === 'no' || m.mode === 'none';
            return (
              <div key={`b${m.id}`} style={sx(S.botWrap)}>
                <div style={sx(S.botRow)}>
                  <img src="/aithora-mark.svg" alt="" style={sx(S.mark)} />
                  <div style={sx(S.botCol)}>
                    {m.paras.map((p, k) => (
                      <div key={k} style={sx(S.para)}>
                        {p}
                      </div>
                    ))}
                    {goPages.length > 0 && (
                      <div style={sx(S.goRow)}>
                        {goPages.map((g) => (
                          <button key={g.view} type="button" className="dlink hx-go" style={sx(S.go)} onClick={() => go({ label: g.name, view: g.view })}>
                            Go to {g.name} {'→'}
                          </button>
                        ))}
                      </div>
                    )}
                    <div style={sx(S.foot)}>
                      {m.mode !== 'none' && m.sources.length > 0 && (
                        <>
                          <span style={sx(S.tag)}>{m.mode === 'ai' ? 'AI answer · source' : 'Source'}</span>
                          {m.sources.map((s) => (
                            <button key={s.label} type="button" className="dlink hx-src" style={sx(S.src)} onClick={() => go(s)}>
                              {s.label}
                            </button>
                          ))}
                        </>
                      )}
                      <span style={sx('flex:1 1 10px;')} />
                      {showFb && (
                        <span style={sx('display:inline-flex;align-items:center;gap:2px;')}>
                          <button type="button" className="dlink hx-yes" title="This helped" aria-label="This helped" style={sx(S.fbBtn)} onClick={() => feedback(m.id, 'yes')}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M7 11v9H4v-9h3Z" />
                              <path d="M7 11l4-7a2 2 0 0 1 2 2v4h5.5a2 2 0 0 1 2 2.3l-1.2 6.5A2 2 0 0 1 17.3 20H7" />
                            </svg>
                          </button>
                          <button type="button" className="dlink hx-no" title="This did not help" aria-label="This did not help" style={sx(S.fbBtn)} onClick={() => feedback(m.id, 'no')}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M17 13V4h3v9h-3Z" />
                              <path d="M17 13l-4 7a2 2 0 0 1-2-2v-4H5.5a2 2 0 0 1-2-2.3l1.2-6.5A2 2 0 0 1 6.7 4H17" />
                            </svg>
                          </button>
                        </span>
                      )}
                      {m.fb === 'yes' && <span>Thanks for telling us.</span>}
                    </div>
                    {follow.length > 0 && (
                      <div style={sx(S.follow)}>
                        {follow.map((f) => (
                          <button key={f.label} type="button" className="dlink hx-link2" style={sx(S.followItem)} onClick={() => ask(f.label, f.preset)}>
                            <span style={sx('flex:1 1 auto;min-width:0;')}>{f.label}</span>
                            <span style={sx('flex:0 0 auto;color:#2F5C97;')} aria-hidden="true">
                              {'↗'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {contact && (
                      <div style={sx(S.contact)}>
                        Still stuck? A person can help:{' '}
                        <a href={mailto(q)} style={sx(S.contactLink)}>
                          {CONTACT}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {busy && (
            <div style={sx('flex:0 0 auto;display:block;')}>
              <div style={sx(S.busy)}>
                <img src="/aithora-mark.svg" alt="" style={sx('width:22px;height:22px;flex:0 0 22px;object-fit:contain;')} />
                <div>
                  <span style={sx(S.dots)} role="status" aria-label="Answering">
                    {[0, 1, 2].map((i) => (
                      <span key={i} style={sx(S.dot(i))} />
                    ))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        <div style={sx(S.bottom)}>
          {auto.length > 0 && (
            <div style={sx(S.auto)}>
              {auto.map((a) => (
                <button key={a.label} type="button" className="dlink hx-link" style={sx(S.autoItem)} onClick={() => ask(a.label, a.preset)}>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="#54606C" strokeWidth="1.8" strokeLinecap="round" style={sx('flex:0 0 13px;')} aria-hidden="true">
                    <circle cx="8.5" cy="8.5" r="5.5" />
                    <path d="M13 13l4 4" />
                  </svg>
                  <span style={sx('flex:1 1 auto;min-width:0;')}>{a.label}</span>
                </button>
              ))}
            </div>
          )}
          {jump.length > 0 && (
            <div style={sx(S.jumpRow)}>
              <span style={sx(S.jumpHead)}>GO TO</span>
              {jump.map((j) => (
                <button key={j.view} type="button" className="dlink hx-go" style={sx(S.jump)} onClick={() => go({ label: j.name, view: j.view })}>
                  {j.name} {'→'}
                </button>
              ))}
            </div>
          )}
          <div style={sx(S.inputBox)}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask(input);
                }
              }}
              rows={2}
              placeholder={pageName ? `Ask about ${pageName}…` : 'Ask a question…'}
              aria-label={pageName ? `Ask about ${pageName}` : 'Ask a question'}
              style={sx(S.input)}
            />
            <button type="button" className="dlink" title="Ask" aria-label="Ask" disabled={!hasInput || busy} style={sx(S.send(hasInput && !busy))} onClick={() => ask(input)}>
              {'↑'}
            </button>
          </div>
          <div style={sx(S.footNote)}>Enter to send · answers come from Aithora help text only</div>
        </div>
      </div>
    </div>
  );
}
